import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, createElementVNode as _createElementVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "mx-auto min-h-full" }

import {
  ref,
  watch,
  onBeforeMount,
} from 'vue';
import { useRouter } from 'vue-router';
import usePluginStore from '@/store/plugin/plugin';
import InstagramLiveMetadata from '@/models/plugin/instagramLiveMetadata';
import Plugin from '@/models/plugin/plugin';
import SimpleHeader from '@/components/tailwind/header/SimpleHeader.vue';
import { useI18n } from 'vue-i18n';
import SimpleFooter from '@/components/tailwind/footer/SimpleFooter.vue';

const PAGE_LIMIT = 100;

/*
  Methods
*/

export default /*@__PURE__*/_defineComponent({
  __name: 'ManagerLayout',
  setup(__props) {

const router = useRouter();
const { t } = useI18n();

const {
  pluginsDoGetMy,
  pluginGetSharedWithMeLiveMetadatas,
} = usePluginStore();

/*
  Data
*/
const dataLoaded = ref(false);
const isLoading = ref(true);
const isLoadingMore = ref(false);
const plugins = ref([] as Plugin[]);
const newPlugins = ref([] as Plugin[]);
const metadatasSharedWithMe = ref([] as InstagramLiveMetadata[]);
const currentPage = ref(1);
const hasMorePages = ref(false);
// Server-side account-handle search term. Empty = full list. Drives every
// fetch (page 1 + load-more) so the search runs on the backend instead of
// filtering the whole loaded list client-side.
const searchTerm = ref('');
// Server-side sort state. Defaults mirror the view's toolbar (username A→Z)
// so the first page already holds the correct top rows — with pagination the
// backend decides WHICH plugins are on page 1, so a mismatch here would show
// the wrong subset, not merely the right subset in the wrong order.
const orderBy = ref('name');
const order = ref(1);
// Total matches reported by the backend for the current query — lets the
// screen show "N de M resultados" without loading everything.
const totalCount = ref(0);
const redirectDashboard = ((error: string) => router.push({
  name: 'DashboardPage',
  query: {
    message: 'Não foi possível carregar a página. Tente novamente em instantes.',
    detail: error,
    type: 'error',
  },
}));
const loadPlugins = (page: number = 1) => {
  if (page > 1) {
    isLoadingMore.value = true;
  }

  pluginsDoGetMy(
    undefined,
    undefined,
    undefined,
    page,
    PAGE_LIMIT,
    searchTerm.value,
    orderBy.value,
    order.value,
  )
    .then((response) => {
      if (response !== undefined) {
        // Combine Instagram and Facebook plugins
        const allPlugins = [
          ...response.instagramPlugins.map((p: any) => new Plugin(p)),
          ...response.facebookPlugins.map((p: any) => new Plugin(p)),
        ].sort((a, b) => a.name.localeCompare(b.name));

        if (page === 1) {
          plugins.value = allPlugins;
        } else {
          plugins.value = [...plugins.value, ...allPlugins];
        }

        currentPage.value = page;
        hasMorePages.value = response.pagination
          ? response.pagination.currentPage < response.pagination.lastPage
          : false;
        totalCount.value = response.pagination?.total ?? allPlugins.length;
      }
    }).catch((error) => {
      console.error('error', error);
      redirectDashboard(error.toString());
    }).finally(() => {
      isLoadingMore.value = false;
    });
};
const loadMorePlugins = () => {
  if (isLoadingMore.value || !hasMorePages.value) return;
  loadPlugins(currentPage.value + 1);
};
// New search term from the toolbar (already debounced in the view). Reset to
// page 1 so results replace the list instead of appending.
const onSearch = (term: string) => {
  searchTerm.value = term.trim();
  loadPlugins(1);
};
// New sort from the toolbar. Refetch page 1 so the backend returns the
// correct top rows for the chosen field/direction (not just a client
// reorder of whatever happened to be loaded).
const onSort = (payload: { orderBy: string; order: number }) => {
  orderBy.value = payload.orderBy;
  order.value = payload.order;
  loadPlugins(1);
};
const loadMetadatasSharedWithMe = () => {
  pluginGetSharedWithMeLiveMetadatas({
    page: 1,
    limit: 100,
    orderBy: 'createdAt',
    order: -1,
  })
    .then((response) => {
      if (response !== undefined) {
        metadatasSharedWithMe.value = response.data;
      }
    }).catch((error) => {
      console.error('error', error);
      redirectDashboard(error.toString());
    });
};
const load = async () => {
  isLoading.value = true;
  await loadPlugins();
  await loadMetadatasSharedWithMe();

  isLoading.value = false;
  dataLoaded.value = true;
};

/*
  Hooks
*/
onBeforeMount(async () => {
  await load();
});
watch(newPlugins, () => {
  load();
});

return (_ctx: any,_cache: any) => {
  const _component_RouterView = _resolveComponent("RouterView")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createVNode(SimpleHeader, {
        "show-close": false,
        pages: [
        { name: _unref(t)('sidemenu.navigation.integration.title'), href: _unref(router).resolve({ name: 'PluginsManagerPage' }).href, current: true },
      ]
      }, null, 8, ["pages"]),
      _createVNode(_component_RouterView, {
        plugins: plugins.value,
        "data-loaded": dataLoaded.value,
        "is-loading-more": isLoadingMore.value,
        "has-more-pages": hasMorePages.value,
        "total-count": totalCount.value,
        onLoadMore: loadMorePlugins,
        onSearch: onSearch,
        onSort: onSort
      }, null, 8, ["plugins", "data-loaded", "is-loading-more", "has-more-pages", "total-count"])
    ]),
    _createVNode(SimpleFooter, { horizontal: true })
  ], 64))
}
}

})